import React, { ReactElement, useState } from "react";
import classNames from "classnames";
import { UiaIcon } from "@rotorstudios/fe-ui";
import { CAVQtvr } from "@rotorstudios/configurable-asset-visualiser";
import { RichText, Typography } from "@tmca/toyota-kit";

import useUiMCMEQtvr from "./useUiMCMEQtvr";
import {
  msgZoomExplore,
  msgZoomIn,
  msgZoomOut,
  msgZoomPinch,
} from "./UiMCMEQtvrZoomMsgs";
import useGA from "../../modules/fe-modules/GA/useGA";
import { GaEvent } from "../../modules/fe-modules/GA/static";
import useGesture from "../../hooks/useGesture";
import UiMCMEQtvrOverlay from "./UiMCMEQtvrOverlay";
import { UI_MODULE_CONFIGURATOR } from "../../modules/Data/static";
import { useDataContext } from "../../modules/Data/DataProvider";
import "./_UiMCMEQtvr.scss";

type UiMCMEQtvrProps = {
  assetSet?: string;
  showZoomControls?: boolean;
  tDisclaimer?: string;
  onOverlayClick?: () => void;
  hasMobilePoster?: boolean;
  mobilePosterCropToken?: string;
};

const UiMCMEQtvr = ({
  assetSet,
  showZoomControls = true,
  tDisclaimer = "",
  onOverlayClick,
  hasMobilePoster,
  mobilePosterCropToken,
}: UiMCMEQtvrProps): ReactElement => {
  const [infoOpen, setInfoOpen] = useState(false);
  const { triggerGAEvent } = useGA();
  const { breakpointActive, scrollZoom, projectClass, assets } = useUiMCMEQtvr({
    assetSet,
  });
  const { modalState, isQtvrOverlayVisible, updateIsQtvrOverlayVisible } =
    useDataContext();

  const getClassName = () => `${projectClass}_UiMCMEQtvr ${breakpointActive}`;

  const handleToggleInfoOpen = () => setInfoOpen(!infoOpen);

  const getInfoButton = () => (
    <button
      className={"InfoButton"}
      onClick={handleToggleInfoOpen}
      data-button-name={`vehicle interior view | ${
        infoOpen ? `close info` : `open info`
      }`}
    >
      {infoOpen ? (
        <UiaIcon icon="_assets/icons/icon_panoscan_info_close.svg" />
      ) : (
        <UiaIcon icon="_assets/icons/icon_panoscan_info.svg" />
      )}
    </button>
  );

  const getInfoOverlay = () => (
    <div className={classNames("InfoOverlay", { Hide: !infoOpen })}>
      <button
        className={classNames("InfoOverlayClose", { Hide: !infoOpen })}
        onClick={handleToggleInfoOpen}
      />
      <div className={classNames("InfoArrows", breakpointActive)}>
        <div className={"InfoArrowsMessage"}>{msgZoomExplore}</div>
      </div>
      <div className={classNames("InfoZoom", breakpointActive)}>
        <div>{msgZoomIn}</div>
        <div>{msgZoomOut}</div>
      </div>
      <div className={classNames("InfoPinch", breakpointActive)}>{msgZoomPinch}</div>
    </div>
  );

  const getImageDisclaimer = (): JSX.Element | null =>
    tDisclaimer ? (
      <div className={classNames("ImageDisclaimer", breakpointActive)}>
        <Typography className="ImageDisclaimerMessage" variant="d1" component="div">
          <RichText html={tDisclaimer} />
        </Typography>
      </div>
    ) : null;

  const viewName = "Toyota For You - toyota connected services";

  const handleInteract3D = (event: React.MouseEvent<HTMLElement>) => {
    let zoomOrPan = "Pan";
    if (event.target instanceof HTMLButtonElement) {
      if (event.target.closest(".ButtonZoomIn")) {
        zoomOrPan = "Zoom In";
      }
      if (event.target.closest(".ButtonZoomOut")) {
        zoomOrPan = "Zoom Out";
      }
    }
    triggerGAEvent(GaEvent.CONFIGURATOR_INTERACT_3D, {
      event: GaEvent.CONFIGURATOR_INTERACT_3D,
      interactionName: `Interior - ${zoomOrPan}`,
      viewName,
      viewSection: `toyota connected services - Interior - ${zoomOrPan}`,
    });
  };

  const handleZoomIn = () => {
    triggerGAEvent(GaEvent.CONFIGURATOR_INTERACT_3D, {
      event: GaEvent.CONFIGURATOR_INTERACT_3D,
      interactionName: `Interior - Zoom In`,
      viewName,
      viewSection: `toyota connected services - Interior - Zoom In`,
    });
  };

  const handleZoomOut = () => {
    triggerGAEvent(GaEvent.CONFIGURATOR_INTERACT_3D, {
      event: GaEvent.CONFIGURATOR_INTERACT_3D,
      interactionName: `Interior - Zoom Out`,
      viewName,
      viewSection: `toyota connected services - Interior - Zoom Out`,
    });
  };

  const handlePan = () => {
    triggerGAEvent(GaEvent.CONFIGURATOR_INTERACT_3D, {
      event: GaEvent.CONFIGURATOR_INTERACT_3D,
      interactionName: `Interior - Pan`,
      viewName,
      viewSection: `toyota connected services - Interior - Pan`,
    });
  };

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useGesture({
    onZoomIn: handleZoomIn,
    onZoomOut: handleZoomOut,
    onPan: handlePan,
  });

  const handleOverlayClick = () => {
    updateIsQtvrOverlayVisible(false);

    const overlay = document.querySelector(".UiMCMEQtvrOverlay");
    const parent = overlay?.parentElement?.parentElement;

    let isStandaloneModule: boolean = false;

    if (parent?.classList) {
      isStandaloneModule = ![...parent.classList].some((className) =>
        className.includes(UI_MODULE_CONFIGURATOR),
      );
    }

    if (isStandaloneModule && onOverlayClick) {
      onOverlayClick();
    }
  };

  const getPosterSrc = (): string => {
    const src = assets[0].sources[0].src;
    return `${src}?cropToken=${mobilePosterCropToken}`;
  };

  return assets ? (
    <div
      className={`${getClassName()}`}
      role="figure"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onMouseDown={handleInteract3D}
    >
      <UiMCMEQtvrOverlay
        onClick={handleOverlayClick}
        isVisible={isQtvrOverlayVisible}
        imageDisclaimer={getImageDisclaimer()}
      />

      {hasMobilePoster && mobilePosterCropToken ? (
        <div className="poster">
          <img src={getPosterSrc()} alt="360 degrees interior" />
        </div>
      ) : (
        <>
          <CAVQtvr
            key={modalState.isVisible ? "modal-open" : "modal-closed"}
            data={assets}
            showZoomControls={showZoomControls}
            scrollZoom={scrollZoom}
          />

          {getInfoButton()}
          {getInfoOverlay()}
          {getImageDisclaimer()}
        </>
      )}
    </div>
  ) : (
    <></>
  );
};

export default UiMCMEQtvr;
